import { GadgetLogoMark } from "@gadgetinc/widgets/src/icons/GadgetLogo";
/* eslint-disable @typescript-eslint/no-misused-promises */
import { Button, Row, expandBorderBottom, expandBorderRadii, expandPadding } from "@gadgetinc/widgets";
import { useStyletron } from "baseui";
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader } from "baseui/modal";
import { SIZE, StyledSpinnerNext } from "baseui/spinner";
import { Textarea } from "baseui/textarea";
import { LabelSmall } from "baseui/typography";
import { isNil } from "lodash";
import { observer } from "mobx-react-lite";
import React, { Suspense, useCallback, useState } from "react";
import Gravatar from "react-gravatar";
import type { ChatMessage } from "web/src/client-state-trees/ChatbotSession";
import { Column } from "@gadgetinc/widgets";
import { CenteredSpin } from "web/src/components/chrome/CenteredSpin";
import { useHover } from "web/src/lib/hooks";
import { DocLink } from "../../markdown-components";

// dynamic import so that we don't need to require the ESM-only react-markdown package server side
const MarkdownRenderer = React.lazy(() => import("web/src/components/markdown/Renderer"));

export const ChatbotChatMessage = observer(({ message, user }: { message: ChatMessage; user?: { email: string } }) => {
  const [css, $theme] = useStyletron();
  const [hovered, hoverProps] = useHover();
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const close = useCallback(() => setFeedbackModalOpen(false), []);
  const submitUserRating = useCallback((isGood?: boolean, comment?: string) => {
    if (!isNil(isGood)) message.setUserRating(isGood);
    if (comment) message.setUserRatingComment(comment);
    void message.saveRating();
  }, []);

  return (
    <article
      key={message.key}
      className={css({
        marginTop: $theme.sizing.scale400,
        marginBottom: $theme.sizing.scale400,
        ...expandBorderBottom($theme.borders.border100),
        display: "flex",
        flexDirection: "row",
        position: "relative",
      })}
      {...hoverProps}
    >
      <div className={css({ width: $theme.sizing.scale800, marginRight: $theme.sizing.scale600, flexShrink: 0 })}>
        {message.role == "user" && (
          <Gravatar
            email={user?.email ?? "anonymous@gadget.dev"}
            size={24}
            rating="pg"
            default={process.env.NODE_ENV === "test" ? "mp" : "monsterid"}
            className={css({
              ...expandBorderRadii("50%"),
            })}
          />
        )}
        {message.role == "system" && <GadgetLogoMark />}
      </div>
      <Column $style={{ flexGrow: 1, minHeight: "42px" }}>
        {message.message.length == 0 && message.loading && <StyledSpinnerNext $size={SIZE.small} />}
        <Suspense fallback={<CenteredSpin />}>{message.message && <MarkdownRenderer markdown={message.message} />}</Suspense>
        {message.showReferences && (
          <ReferencesContainer>
            <LabelSmall>References:</LabelSmall>
            {message.references.map((reference) => (
              <ReferenceCard key={reference.title} reference={reference} />
            ))}
          </ReferencesContainer>
        )}
      </Column>
      {message.rateable && hovered && (
        <Row
          $as="aside"
          $gap={$theme.sizing.scale200}
          $style={{
            position: "absolute",
            bottom: $theme.sizing.scale200,
            right: $theme.sizing.scale200,
            ...expandPadding($theme.sizing.scale200),
            backgroundColor: $theme.colors.backgroundSecondary,
            boxShadow: $theme.lighting.shadow400,
            ...expandBorderRadii($theme.borders.radius200),
          }}
        >
          <Button
            kind="secondary"
            onClick={() => {
              submitUserRating(true);
              setFeedbackModalOpen(true);
            }}
            $style={{ color: $theme.colors.contentPositive, textDecoration: "none" }}
          >
            +1
          </Button>
          <Button
            kind="secondary"
            onClick={() => {
              submitUserRating(false);
              setFeedbackModalOpen(true);
            }}
            $style={{ color: $theme.colors.contentNegative, textDecoration: "none" }}
          >
            -1
          </Button>
        </Row>
      )}
      <Modal isOpen={!!feedbackModalOpen} onClose={close}>
        <ModalHeader>{message.userRating ? "Submit message feedback" : "Report message"}</ModalHeader>
        <ModalBody>
          <Textarea
            placeholder={
              message.userRating === true ? "Tell us how we can make this message even better" : "Tell us how we can improve this message"
            }
            value={message.userRatingComment ?? ""}
            onChange={(e) => message.setUserRatingComment(e.currentTarget.value)}
          />
        </ModalBody>
        <ModalFooter>
          <ModalButton kind="tertiary" onClick={close}>
            Cancel
          </ModalButton>
          <ModalButton
            onClick={() => {
              submitUserRating();
              close();
            }}
          >
            Save
          </ModalButton>
        </ModalFooter>
      </Modal>
    </article>
  );
});

export const ReferencesContainer = (props: { children: React.ReactNode }) => {
  const [_css, $theme] = useStyletron();
  return (
    <Row
      $gap={$theme.sizing.scale200}
      style={{
        marginLeft: $theme.sizing.scale400,
        marginRight: $theme.sizing.scale400,
        marginTop: $theme.sizing.scale200,
        marginBottom: $theme.sizing.scale200,
      }}
    >
      {props.children}
    </Row>
  );
};

export const ReferenceCard = (props: { reference: { title: string; url: string } }) => {
  const [css, $theme] = useStyletron();
  return (
    // eslint-disable-next-line react/jsx-no-target-blank
    <DocLink
      href={props.reference.url}
      $style={{
        display: "block",
        ...expandPadding($theme.sizing.scale100),
        ...$theme.typography.LabelSmall,
        whiteSpace: "nowrap",
      }}
    >
      {props.reference.title}
    </DocLink>
  );
};
